/**
 * Data Import Pipeline service — matches /api/v1/imports routes.
 */
import api from './api';
import { ApiResponse } from '../types';
import {
  BatchStatusResponse,
  BusinessCardImage,
  BusinessCardReviewRequest,
  CommitRequest,
  CommitResponse,
  ImportBatch,
  ImportBatchList,
  ParseMappingResponse,
  PreviewResponse,
  SourceType,
} from '../types/importBatch';

export async function uploadImportFile(
  file: File,
  sourceType?: SourceType
): Promise<ApiResponse<ImportBatch>> {
  try {
    const fd = new FormData();
    fd.append('file', file);
    if (sourceType) fd.append('source_type', sourceType);
    const response = await api.post('/imports/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { data: response.data };
  } catch (err: any) {
    return {
      error: err.response?.data?.detail || 'Failed to upload import file',
    };
  }
}

export async function uploadBusinessCards(
  files: File[]
): Promise<ApiResponse<ImportBatch>> {
  try {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    const response = await api.post('/imports/business-cards', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { data: response.data };
  } catch (err: any) {
    return {
      error: err.response?.data?.detail || 'Failed to upload business cards',
    };
  }
}

export async function getMapping(
  batchId: number
): Promise<ApiResponse<ImportBatch>> {
  try {
    const response = await api.get(`/imports/batches/${batchId}/status`);
    return { data: response.data };
  } catch (err: any) {
    return { error: err.response?.data?.detail || 'Failed to load mapping' };
  }
}

export async function saveMapping(
  batchId: number,
  fieldMapping: Record<string, string>
): Promise<ApiResponse<ImportBatch>> {
  try {
    const response = await api.post(`/imports/batches/${batchId}/mapping`, {
      field_mapping: fieldMapping,
    });
    return { data: response.data };
  } catch (err: any) {
    return { error: err.response?.data?.detail || 'Failed to save mapping' };
  }
}

export async function getPreview(
  batchId: number
): Promise<ApiResponse<PreviewResponse>> {
  try {
    const response = await api.get(`/imports/batches/${batchId}/preview`);
    return { data: response.data };
  } catch (err: any) {
    return { error: err.response?.data?.detail || 'Failed to load preview' };
  }
}

export async function commit(
  batchId: number,
  payload: CommitRequest
): Promise<ApiResponse<CommitResponse>> {
  try {
    const response = await api.post(
      `/imports/batches/${batchId}/commit`,
      payload
    );
    return { data: response.data };
  } catch (err: any) {
    return { error: err.response?.data?.detail || 'Failed to commit import' };
  }
}

export async function getStatus(
  batchId: number
): Promise<ApiResponse<BatchStatusResponse>> {
  try {
    const response = await api.get(`/imports/batches/${batchId}/status`);
    return { data: response.data };
  } catch (err: any) {
    return { error: err.response?.data?.detail || 'Failed to load status' };
  }
}

export async function listBatches(params?: {
  page?: number;
  size?: number;
  source_type?: SourceType;
  status?: string;
}): Promise<ApiResponse<ImportBatchList>> {
  try {
    const response = await api.get('/imports/batches', { params });
    return { data: response.data };
  } catch (err: any) {
    return { error: err.response?.data?.detail || 'Failed to load batches' };
  }
}

export async function bumpPriority(
  batchId: number,
  priority: number
): Promise<ApiResponse<{ batch_id: number; research_priority: number }>> {
  try {
    const response = await api.post(`/imports/batches/${batchId}/priority`, {
      research_priority: priority,
    });
    return { data: response.data };
  } catch (err: any) {
    return { error: err.response?.data?.detail || 'Failed to update priority' };
  }
}

export async function listBusinessCards(
  batchId: number
): Promise<ApiResponse<BusinessCardImage[]>> {
  try {
    const response = await api.get(
      `/imports/batches/${batchId}/business-cards`
    );
    return { data: response.data };
  } catch (err: any) {
    return {
      error: err.response?.data?.detail || 'Failed to load business cards',
    };
  }
}

export async function reviewCard(
  imageId: number
): Promise<ApiResponse<BusinessCardImage>> {
  try {
    const response = await api.get(
      `/imports/business-cards/${imageId}/review`
    );
    return { data: response.data };
  } catch (err: any) {
    return { error: err.response?.data?.detail || 'Failed to load card' };
  }
}

export async function updateCard(
  imageId: number,
  payload: BusinessCardReviewRequest
): Promise<ApiResponse<BusinessCardImage>> {
  try {
    const response = await api.patch(
      `/imports/business-cards/${imageId}`,
      payload
    );
    return { data: response.data };
  } catch (err: any) {
    return { error: err.response?.data?.detail || 'Failed to update card' };
  }
}

export async function parseAndSuggestMapping(
  batchId: number
): Promise<ApiResponse<ParseMappingResponse>> {
  // The upload endpoint already ran parse_and_suggest_mapping, but we expose
  // this re-fetch via the status endpoint which returns the saved mapping.
  return getMapping(batchId) as unknown as Promise<ApiResponse<ParseMappingResponse>>;
}

const importService = {
  uploadImportFile,
  uploadBusinessCards,
  getMapping,
  saveMapping,
  getPreview,
  commit,
  getStatus,
  listBatches,
  bumpPriority,
  listBusinessCards,
  reviewCard,
  updateCard,
  parseAndSuggestMapping,
};

export default importService;
